import React, { useState, useEffect } from 'react';
import RegistrationModal from '../registration/RegistrationModal';
import AutomaticSeatingForm from './AutomaticSeatingForm';
import { CategoryListingSkeleton } from '../../common/SkeletonLoader';
import './V4CategoriesListing.scss';

interface Category {
  id: number;
  name: string;
  price: number;
  price_display: string;
  description: string;
  quantity: number;
  sold_count: number;
  remaining: number;
  sold_out: boolean;
  hidden: boolean;
  highlight: boolean;
  registration_type: string;
  tag: string;
  priority: number;
  currency?: {
    symbol: string;
  };
  show_any_amount?: boolean;
}

interface Cluster {
  id: number;
  name: string;
  description: string;
  color: string;
  icon: string;
  display_order: number;
  is_active: boolean;
  categories: Category[];
  price_range?: string;
}

interface OrganizedCategories {
  unclustered: Category[];
  clustered: Record<string, Cluster>;
}

interface V4CategoriesListingProps {
  event: any;
  user?: any;
  loading?: boolean;
  error?: string | null;
  onManualSeating?: () => void;
}

const V4CategoriesListing: React.FC<V4CategoriesListingProps> = ({ 
  event, 
  user, 
  loading = false, 
  error = null,
  onManualSeating
}) => {
  const [collapsedClusters, setCollapsedClusters] = useState<Set<number>>(new Set());
  const [showAutomaticSeatingForm, setShowAutomaticSeatingForm] = useState(false);
  const [registrationModal, setRegistrationModal] = useState<{
    show: boolean;
    category: Category | null;
    type: 'general' | 'individual' | 'donation' | 'whitelist' | 'team' | 'seated';
  }>({
    show: false,
    category: null,
    type: 'general'
  });

  // Initialize all clusters as collapsed by default
  useEffect(() => {
    if (event && event.organized_categories && event.organized_categories.clustered) {
      const allClusterIds = Object.keys(event.organized_categories.clustered).map(id => parseInt(id));
      setCollapsedClusters(new Set(allClusterIds));
    }
  }, [event]);


  if (loading) {
    return (
      <div className="v4-categories-listing">
        <CategoryListingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="v4-categories-listing">
        <div className="alert alert-warning" role="alert">
          <strong>Categories Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  // Check if categories should be displayed
  const shouldShowCategories = () => {
    const organizedCategories = event.organized_categories;
    if (!organizedCategories) {
      return false;
    }

    const hasUnclustered = organizedCategories.unclustered && organizedCategories.unclustered.length > 0;
    const hasClustered = organizedCategories.clustered && Object.keys(organizedCategories.clustered).length > 0;
    
    return hasUnclustered || hasClustered;
  };

  if (!shouldShowCategories()) {
    return null;
  }

  const organizedCategories: OrganizedCategories = event.organized_categories;

  const formatPrice = (category: Category) => {
    if (category.price === 0) {
      return 'Free';
    }
    
    if (category.show_any_amount) {
      return category.price_display || `$${category.price.toFixed(2)}`;
    }
    
    const symbol = category.currency?.symbol || '$';
    return `${symbol} ${category.price.toLocaleString()}`;
  };

  const toggleCluster = (clusterId: number) => {
    const newCollapsed = new Set(collapsedClusters);
    if (newCollapsed.has(clusterId)) {
      newCollapsed.delete(clusterId);
    } else {
      newCollapsed.add(clusterId);
    }
    setCollapsedClusters(newCollapsed);
  };

  const handleRegistrationClick = (category: Category) => {
    // Check if this is a seated event and category
    if (event.setting.seated && category.registration_type?.toLowerCase() === 'seated') {
      // Navigate to seated registration page
      window.location.href = `/events/${event.slug}/seated/${category.id}`;
      return;
    }

    // Determine registration type based on category properties
    let registrationType: 'general' | 'individual' | 'donation' | 'whitelist' | 'team' | 'seated' = 'general';
    
    switch (category.registration_type?.toLowerCase()) {
      case 'individual':
        registrationType = 'individual';
        break;
      case 'donation':
        registrationType = 'donation';
        break;
      case 'whitelisted':
        registrationType = 'whitelist';
        break;
      case 'team':
        registrationType = 'team';
        break;
      case 'seated':
        registrationType = 'seated';
        break;
      case 'direct':
      case 'pay_for_friend':
      case 'general':
      default:
        registrationType = 'general';
        break;
    }
    
    if (category.show_any_amount && category.price === 0) {
      registrationType = 'donation';
    }
    
    setRegistrationModal({
      show: true,
      category,
      type: registrationType
    });
  };

  const handleCloseModal = () => {
    setRegistrationModal({
      show: false,
      category: null,
      type: 'general'
    });
  };

  const handleAutomaticSeating = () => {
    // Open automatic seating form directly
    setShowAutomaticSeatingForm(true);
  };

  const handleAutomaticSeatingSuccess = (result: any) => {
    setShowAutomaticSeatingForm(false);
    if (result.redirect_url) {
      window.location.href = result.redirect_url;
    } else {
      // Show success message or redirect to cart
      window.location.href = '/cart/';
    }
  };

  const handleAutomaticSeatingError = (error: string) => {
    console.error('Automatic seating error:', error);
    // Error is handled within the form component
  };

  const handleAutomaticSeatingCancel = () => {
    setShowAutomaticSeatingForm(false);
  };

  const handleManualSeating = () => {
    if (onManualSeating) {
      onManualSeating();
    } else {
      // Scroll to image map or focus on it
      const imageMapSection = document.querySelector('.image-map-section') as HTMLElement;
      if (imageMapSection) {
        imageMapSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        
        // Add a subtle highlight effect
        imageMapSection.style.transition = 'all 0.3s ease';
        imageMapSection.style.boxShadow = '0 0 20px rgba(242, 63, 57, 0.3)';
        setTimeout(() => {
          imageMapSection.style.boxShadow = '';
        }, 2000);
      }
    }
  };


  const renderCategoryCard = (category: Category) => {
    const isSoldOut = category.sold_out || category.remaining <= 0;
    const canRegister = event.setting.live && !event.setting.on_page_registration && !isSoldOut;
    
    return (
      <div 
        key={category.id} 
        className={`category-card ${category.highlight ? 'highlighted' : ''} ${isSoldOut ? 'sold-out' : ''}`}
      >
        {canRegister ? (
          <a 
            href="#" 
            className="category-card-link"
            onClick={(e) => {
              e.preventDefault();
              handleRegistrationClick(category);
            }}
          >
            <div className="category-header">
              <div className="category-info">
                <h4 className="category-name">{category.name}</h4>
                <div className="price-container">
                  <span className="price-tag">{formatPrice(category)}</span>
                  <div className="category-action-btn">
                    <i className="fas fa-arrow-right"></i>
                  </div>
                </div>
              </div>
            </div>

            {!event.setting.on_page_registration && event.setting.show_remaining_passes && (
              <div className="category-footer">
                <span className="remaining-passes">
                  <i className="fas fa-ticket-alt"></i>
                  {category.remaining} Left
                </span>
              </div>
            )}
          </a>
        ) : (
          <>
            <div className="category-header">
              <div className="category-info">
                <h4 className="category-name">{category.name} ({isSoldOut && (
                  <>
                      {event.content.sold_out_text || 'Sold Out'}
                    </>
                  )})</h4>
                <div className="price-container">
                  <span className="price-tag">{formatPrice(category)}</span>
                  <div className="category-action-btn sold-out-btn">
                    <i className="fas fa-times-circle"></i>
                  </div>
                </div>
              </div>
            </div>

            {!event.setting.on_page_registration && !isSoldOut && event.setting.show_remaining_passes && (
              <div className="category-footer">
                <span className="remaining-passes">
                  <i className="fas fa-ticket-alt"></i>
                  {category.remaining} Left
                </span>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="v4-categories-listing">
      {/* Automatic Seating Button - only show if event is live */}
      {event.setting.live && (
        <button 
          className="cluster-selection-btn w-100 mb-3"
          onClick={handleAutomaticSeating}
        >
          <i className="fas fa-ticket-alt"></i>
          <span>Automatically Pick My Seats</span>
          <i className="fas fa-arrow-right"></i>
        </button>
      )}

      <div className="categories-container">
        {/* First show unclustered categories */}
        {organizedCategories.unclustered && organizedCategories.unclustered.length > 0 && (
          <>
            {organizedCategories.unclustered.map((category: Category) => 
              !category.hidden ? renderCategoryCard(category) : null
            )}
          </>
        )}

        {/* Then show clustered categories grouped by cluster */}
        {organizedCategories.clustered && Object.keys(organizedCategories.clustered).length > 0 && (
          <>
            {Object.entries(organizedCategories.clustered).map(([clusterId, cluster]: [string, Cluster]) => (
              <div key={clusterId} className="category-group">
                <div 
                  className={`category-group-header ${collapsedClusters.has(parseInt(clusterId)) ? 'collapsed' : 'expanded'}`}
                  onClick={() => toggleCluster(parseInt(clusterId))}
                >
                  <div>
                    <h5 className="category-group-title">
                      {cluster.icon && <i className={`${cluster.icon} me-2`}></i>}
                      {cluster.name}
                    </h5>
                    {cluster.description && !collapsedClusters.has(parseInt(clusterId)) && (
                      <p className="category-group-description">{cluster.description}</p>
                    )}
                  </div>
                  <div>
                    {cluster.price_range && (
                      <span className="bg-primary rounded-pill px-2 py-1 me-2 text-white">
                        {cluster.price_range}
                      </span>
                    )}
                    <i className={`fas fa-chevron-${collapsedClusters.has(parseInt(clusterId)) ? 'down' : 'up'} category-group-icon`}></i>
                  </div>
                </div>
                <div 
                  className="category-group-content"
                  style={{ display: collapsedClusters.has(parseInt(clusterId)) ? 'none' : 'block' }}
                >
                  {cluster.categories.map((category: Category) => 
                    !category.hidden ? renderCategoryCard(category) : null
                  )}
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Automatic Seating Form */}
      <AutomaticSeatingForm
        show={showAutomaticSeatingForm}
        event={event}
        user={user}
        onSuccess={handleAutomaticSeatingSuccess}
        onError={handleAutomaticSeatingError}
        onCancel={handleAutomaticSeatingCancel}
      />

      {/* Registration Modal */}
      {registrationModal.category && registrationModal.type !== 'seated' && (
        <RegistrationModal
          show={registrationModal.show}
          onHide={handleCloseModal}
          event={event}
          category={registrationModal.category}
          user={user}
          registrationType={registrationModal.type as 'general' | 'individual' | 'donation' | 'whitelist' | 'team'}
        />
      )}
    </div>
  );
};

export default V4CategoriesListing;
